import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setWebBookingGuestEmail } from '../../utils/webPaymentGuest';

type Props = {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  initialEmail?: string | null;
  onSaved?: (email: string) => void;
  mode?: 'guest' | 'clerk';
  /**
   * Channel identity, when the visitor arrives from a messaging channel. When present the
   * modal shows that identity (read-only) as the primary way the user is recognized, and the
   * email becomes optional instead of the only identity.
   */
  whatsappId?: string | null;
  telegramId?: string | null;
};

export function BookingGuestContactModal({
  open,
  onClose,
  tenantId,
  initialEmail,
  onSaved,
  mode = 'guest',
  whatsappId,
  telegramId,
}: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const isClerk = mode === 'clerk';
  // Channel identity is only meaningful for guests (a signed-in user is identified by Clerk).
  const channelId = !isClerk ? (whatsappId?.trim() || telegramId?.trim() || '') : '';
  const channel: 'whatsapp' | 'telegram' | null =
    !isClerk && whatsappId?.trim() ? 'whatsapp' : !isClerk && telegramId?.trim() ? 'telegram' : null;
  const hasChannelIdentity = Boolean(channel);

  useEffect(() => {
    if (open) {
      setEmail((initialEmail || '').trim());
    }
  }, [open, initialEmail]);

  if (!open) return null;

  const emailValid = email.trim().includes('@');
  // With a channel identity the email is optional, so saving/closing is always allowed.
  const canSave = !isClerk && (emailValid || hasChannelIdentity);

  const save = () => {
    if (isClerk) return;
    const trimmed = email.trim().toLowerCase();
    if (trimmed.includes('@')) {
      setWebBookingGuestEmail(tenantId, trimmed);
      onSaved?.(trimmed);
    }
    // No email but identified by channel → nothing to persist, just close.
    onClose();
  };

  const descKey = isClerk
    ? 'booking.account_clerk_profile_desc'
    : channel === 'whatsapp'
      ? 'booking.account_guest_profile_desc_whatsapp'
      : channel === 'telegram'
        ? 'booking.account_guest_profile_desc_telegram'
        : 'booking.account_guest_profile_desc';

  const emailLabelKey = hasChannelIdentity ? 'booking.email_label_optional' : 'booking.email_label';

  return (
    <div className="booking-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="booking-modal-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-guest-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="booking-guest-profile-title" className="booking-modal-title">
          {isClerk ? t('booking.account_clerk_profile_title') : t('booking.account_guest_profile_title')}
        </h2>
        <p className="booking-modal-desc">{t(descKey)}</p>

        {hasChannelIdentity ? (
          <>
            <label className="booking-modal-label" htmlFor="booking-channel-identity">
              {channel === 'whatsapp'
                ? t('booking.whatsapp_number_label')
                : t('booking.telegram_account_label')}
            </label>
            <input
              id="booking-channel-identity"
              type="text"
              className="booking-modal-input"
              value={channelId}
              readOnly
              aria-readonly="true"
            />
          </>
        ) : null}

        <label className="booking-modal-label" htmlFor="booking-guest-email">
          {t(emailLabelKey)}
        </label>
        <input
          id="booking-guest-email"
          type="email"
          className="booking-modal-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          readOnly={isClerk}
          aria-readonly={isClerk}
        />
        <div className="booking-modal-actions">
          <button type="button" className="booking-modal-btn booking-modal-btn--secondary" onClick={onClose}>
            {t('bookings.close')}
          </button>
          {!isClerk ? (
            <button
              type="button"
              className="booking-modal-btn booking-modal-btn--primary"
              onClick={save}
              disabled={!canSave}
            >
              {t('booking.account_guest_profile_save')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
